/* eslint-disable max-lines-per-function */
import { ThreadExtendedActivityFilter, ThreadExtendedActivityTimeframeType } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import {
  ResetAnalyticsSettingsThreadExtendedActivityForCommunityAnnouncement,
  ResetAnalyticsSettingsThreadExtendedActivityForOthers,
  ResetAnalyticsSettingsThreadExtendedActivityForStorylineAnnouncement,
  SetAnalyticsSettingsRetentionCardCollapsed,
  SetAnalyticsSettingsSelectNetworkTimeframe,
  SetAnalyticsSettingsSelectNetworkTopThreadStarterSenderFilter,
  SetAnalyticsSettingsSelectThreadExtendedActivityForCommunityAnnouncement,
  SetAnalyticsSettingsSelectThreadExtendedActivityForOthers,
  SetAnalyticsSettingsSelectThreadExtendedActivityForStorylineAnnouncement,
  SetAnalyticsSettingsSelectTopNetworkAnnouncementSortOrder,
  SetAnalyticsSettingsSelectTopNetworkGroupSortOrder,
  SetAnalyticsSettingsSelectTopNetworkThreadSortOrder,
  SetAnalyticsSettingsSelectedUserAction,
  SetAnalyticsSettingsViewerActivityThreadType,
  SetAnalyticsSettingsViewerActivityTimeframe,
} from '../../domains/analyticsSettings/actions.types';

import { defaultAnalyticsSettings } from './mock';
import { AnalyticsSettings } from './types';

export type AnalyticsSettingsAction =
  | SetAnalyticsSettingsSelectedUserAction
  | SetAnalyticsSettingsSelectNetworkTimeframe
  | SetAnalyticsSettingsSelectNetworkTopThreadStarterSenderFilter
  | SetAnalyticsSettingsSelectTopNetworkAnnouncementSortOrder
  | SetAnalyticsSettingsSelectTopNetworkGroupSortOrder
  | SetAnalyticsSettingsSelectTopNetworkThreadSortOrder
  | SetAnalyticsSettingsRetentionCardCollapsed
  | SetAnalyticsSettingsSelectThreadExtendedActivityForOthers
  | SetAnalyticsSettingsSelectThreadExtendedActivityForStorylineAnnouncement
  | SetAnalyticsSettingsSelectThreadExtendedActivityForCommunityAnnouncement
  | ResetAnalyticsSettingsThreadExtendedActivityForOthers
  | ResetAnalyticsSettingsThreadExtendedActivityForStorylineAnnouncement
  | ResetAnalyticsSettingsThreadExtendedActivityForCommunityAnnouncement
  | SetAnalyticsSettingsViewerActivityThreadType
  | SetAnalyticsSettingsViewerActivityTimeframe;

const initialState = defaultAnalyticsSettings;

const reducer: Reducer<AnalyticsSettings, AnalyticsSettingsAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'ANALYTICS_SETTING_SELECTED_USER.SET':
      return { ...state, selectedOnBehalfOfUserId: action.payload.selectedOnBehalfOfUserId };
    case 'ANALYTICS_SETTING_SELECT_NETWORK_TIMEFRAME.SET':
      return {
        ...state,
        networkActivitySettings: { ...state.networkActivitySettings, timeframe: action.payload.timeframe },
      };
    case 'ANALYTICS_SETTING_SELECT_NETWORK_TOP_THREAD_STARTER_SENDER_FILTER.SET':
      return {
        ...state,
        networkActivitySettings: {
          ...state.networkActivitySettings,
          topThreadStarterSenderFilter: action.payload.filterBy,
        },
      };
    case 'ANALYTICS_SETTING_SELECT_TOP_NETWORK_ANNOUNCEMENT_SORT_ORDER.SET':
      return {
        ...state,
        networkActivitySettings: {
          ...state.networkActivitySettings,
          topNetworkAnnouncementSortOrder: action.payload.sortOrder,
        },
      };
    case 'ANALYTICS_SETTING_SELECT_TOP_NETWORK_GROUP_SORT_ORDER.SET':
      return {
        ...state,
        networkActivitySettings: {
          ...state.networkActivitySettings,
          topNetworkGroupSortOrder: action.payload.sortOrder,
        },
      };
    case 'ANALYTICS_SETTING_SELECT_TOP_NETWORK_THREAD_SORT_ORDER.SET':
      return {
        ...state,
        networkActivitySettings: {
          ...state.networkActivitySettings,
          topNetworkThreadSortOrder: action.payload.sortOrder,
        },
      };
    case 'ANALYTICS_SETTING_SELECT_THREAD_EXTENDED_ACTIVITY_FOR_OTHERS.SET':
      return {
        ...state,
        threadExtendedActivitySettingsForOthers: {
          ...state.threadExtendedActivitySettingsForOthers,
          timeframe: action.payload.timeframe,
          isAnnouncement: action.payload.isAnnouncement,
          filterEntityId: action.payload.filterEntityId,
          filterBy: action.payload.filterBy,
        },
      };
    case 'ANALYTICS_SETTING_SELECT_THREAD_EXTENDED_ACTIVITY_FOR_STORYLINE_ANNOUNCEMENT.SET':
      return {
        ...state,
        threadExtendedActivitySettingsForStorylineAnnouncement: {
          ...state.threadExtendedActivitySettingsForStorylineAnnouncement,
          timeframe: action.payload.timeframe,
          isAnnouncement: action.payload.isAnnouncement,
          filterEntityId: action.payload.filterEntityId,
          filterBy: action.payload.filterBy,
        },
      };
    case 'ANALYTICS_SETTING_SELECT_THREAD_EXTENDED_ACTIVITY_FOR_COMMUNITY_ANNOUNCEMENT.SET':
      return {
        ...state,
        threadExtendedActivitySettingsForCommunityAnnouncement: {
          ...state.threadExtendedActivitySettingsForCommunityAnnouncement,
          timeframe: action.payload.timeframe,
          isAnnouncement: action.payload.isAnnouncement,
          filterEntityId: action.payload.filterEntityId,
          filterBy: action.payload.filterBy,
        },
      };
    case 'ANALYTICS_SETTING_THREAD_EXTENDED_ACTIVITY_FOR_OTHERS.RESET':
      return {
        ...state,
        threadExtendedActivitySettingsForOthers: {
          timeframe: ThreadExtendedActivityTimeframeType.TWENTY_EIGHT_DAYS,
          isAnnouncement: false,
          filterEntityId: undefined,
          filterBy: ThreadExtendedActivityFilter.NONE,
        },
      };
    case 'ANALYTICS_SETTING_THREAD_EXTENDED_ACTIVITY_FOR_STORYLINE_ANNOUNCEMENT.RESET':
      return {
        ...state,
        threadExtendedActivitySettingsForStorylineAnnouncement: {
          timeframe: ThreadExtendedActivityTimeframeType.TWENTY_EIGHT_DAYS,
          isAnnouncement: true,
          filterEntityId: undefined,
          filterBy: ThreadExtendedActivityFilter.NONE,
        },
      };
    case 'ANALYTICS_SETTING_THREAD_EXTENDED_ACTIVITY_FOR_COMMUNITY_ANNOUNCEMENT.RESET':
      return {
        ...state,
        threadExtendedActivitySettingsForCommunityAnnouncement: {
          timeframe: ThreadExtendedActivityTimeframeType.TWENTY_EIGHT_DAYS,
          isAnnouncement: true,
          filterEntityId: undefined,
          filterBy: ThreadExtendedActivityFilter.NONE,
        },
      };
    case 'ANALYTICS_SETTING_VIEWER_ACTIVITY_THREAD_TYPE.SET':
      return {
        ...state,
        viewerActivitySettings: {
          ...state.viewerActivitySettings,
          threadType: action.payload.threadType,
        },
      };
    case 'ANALYTICS_SETTING_VIEWER_ACTIVITY_TIMEFRAME.SET':
      return {
        ...state,
        viewerActivitySettings: {
          ...state.viewerActivitySettings,
          timeframe: action.payload.timeframe,
        },
      };
    case 'ANALYTICS_SETTING_RETENTION_CARD_COLLAPSED.SET':
      return {
        ...state,
        networkActivitySettings: {
          ...state.networkActivitySettings,
          isRetentionCardCollapsed: action.payload.isCollapsed,
        },
      };
    default:
      return state;
  }
};

export default reducer;
